import React, { useState, useEffect, useRef } from 'react';

import { Validators } from 'utils';
import { InputLabel, Input, Button, InputError, Topography, BackButton } from 'components/ui';

import styles from './PasswordForm.module.scss';
import { PasswordFormProps, Passwords, Error } from '../../../types/profile';
import clsx from 'clsx';
import eye_icon from 'assets/images/eye_icon.png'
import eye_close_icon from 'assets/images/eye_close.png';
import { authSelector } from 'store/reducers/auth.reducer';
import { Profile as ProfileType } from 'types';
import { useDispatch, useSelector} from 'react-redux';
import cookies from '../../../store/apis/cookies';
import api2 from 'store/apis/api2';

export const PasswordForm: React.FC<PasswordFormProps> = ({ onSubmit, handleClose }) => {
  const initialState = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };
  const [errors, setErrors] = useState<Error[]>([]);
  const [passwords, setPasswords] = useState<Passwords>(initialState);
  const { profile} = useSelector(authSelector);
  const [showTexts, setShowTexts] = useState<any>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(profile?.is_captcha_enable ? true : false);
  const [captchaError, setCaptchaError] = useState<boolean>(false);
  const [captchaImage, setCaptchaImage] = useState<any>();
  const [captchaText, setCaptchaText] = useState<any>();
  const dispatch = useDispatch();
    // Load captcha on component mount
    useEffect(() => {
      fetchCaptcha();
    }, []);
  const handleCaptchaExpired = () => {
    // This function is called when the reCAPTCHA expires
    setIsCaptchaVerified(true);
  };
  useEffect(() => {
    if (passwords.confirmPassword === '') setErrors(prev => prev.filter(item => item.id !== 'confirmPassword'));
    if (passwords.newPassword === '') setErrors(prev => prev.filter(item => item.id !== 'newPassword'));
  }, [passwords]);

  const validate = (value: string, id: string) => {
    const list: Error[] = [];
    
    if (id === 'currentPassword') return;

    if (id === 'newPassword') {
      const validErrors = Validators.validPassword(value);

      if (validErrors)
        list.push({
          messages: validErrors,
          id: id,
        });
        setErrors(list);
    }
  };

  const isDisable = () => {
    // Add captcha validation check here
    const isCaptchaInvalid = false;

    // Add other validation checks
    const isOtherInvalid = errors.length > 0 || passwords.newPassword === '' || passwords.currentPassword === '';

    // Return true if any validation issue is found
    return isCaptchaInvalid || isOtherInvalid;
  };

  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setPasswords(passwords => ({ ...passwords, [id]: value }));
    if (id ==='confirmPassword'){
      setErrors([]);
      if (value !== passwords.newPassword){
        setErrors(prev => [...prev, { id: 'confirmPassword', messages: ["Passwords don't match"] }]);
      }else{
        setErrors([]);
      }
    }else{
      validate(value, id);
    }
  };
  
  const onSubmitUpdate = () => {
    if (errors.length > 0) return;
  
    const captchaElement = document.getElementById('captcha_input') as HTMLInputElement | null;
  
    if (!captchaElement) {
      console.error('Captcha input element not found');
      setCaptchaError(true);
      setCaptchaError(false); // This line seems redundant — consider removing it if not needed.
      onSubmit(passwords.currentPassword, passwords.newPassword);
      setPasswords(initialState);
      return;
    }
  
    const isCaptchaValid = captchaText === captchaElement.value;
  
    setCaptchaError(!isCaptchaValid);
  
    if (isCaptchaValid) {
      onSubmit(passwords.currentPassword, passwords.newPassword);
      setPasswords(initialState);
    }
  };
  
  

  const onCancelUpdate = () => {
    handleClose()
    setPasswords(initialState);
    if (errors.length > 0) setErrors([]);
  };
  // Function to fetch captcha
  const fetchCaptcha = async () => {
    try {
      const response = await api2.get(`/api/v2/user/${profile?.id}/generate_captcha`, {})(dispatch, false, true);
      setCaptchaImage(response?.captcha_image);
      setCaptchaText(response?.captcha_text);
      console.log('Captcha generated:', response);
    } catch (error) {
      console.error('Error generating captcha:', error);
      // Optionally display an error message to the user
    }
  };
  const inputs = [
    { value: passwords.currentPassword, label: 'Current Password', id: 'currentPassword' },
    { value: passwords.newPassword, label: 'New Password', id: 'newPassword' },
    { value: passwords.confirmPassword, label: 'Confirm New Password', id: 'confirmPassword' },
  ];
  const toggleShowPassword = (fieldId:any) => {
    setShowTexts({ ...showTexts, [fieldId]: !showTexts[fieldId] });
  };
  const allowNumberOnly = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " " && e.currentTarget.value.length === 0) {
      e.preventDefault(); // Prevent space at the beginning
    }
    
    if (!/^[0-9]$/.test(e.key) && e.key !== "Backspace" && e.key !== "Tab") {
      e.preventDefault(); // Allow only numbers, Backspace, and Tab
    }
  };
  const renderInputs = () => {
    return (
      <div className='col-12 col-md-12'>
        <div className='row'>
          {inputs.map(item => (
            <div key={item.id} className='col-12 mt-2 change_password_wrapper'>
              <InputLabel text={item.label} />
              <Input
                type={showTexts[item.id] ? 'text' : 'password'}
                value={item.value}
                onChange={onChangeValue}
                id={item.id}
                placeholder='Required'
              />
              <span className='input_icon'>
                <img 
                src={showTexts[item.id] ? eye_close_icon : eye_icon}
                onClick={() => toggleShowPassword(item.id)}
                style={{ cursor: 'pointer' }}
                />
              </span>

              {errors.map(error =>
                error.id === item.id && error.messages.length > 0 ? (
                  <div className={styles['valid-messages']}>
                    {error.id === 'newPassword' && <div>Password must include:</div>}
                    {error.messages.map(message => (
                      <InputError className={styles.error} key={message}>
                        {message}
                      </InputError>
                    ))}
                  </div>
                ) : null,
              )}
            </div>
          ))}
        </div>
        {profile?.is_captcha_enable ? (
           <div className="recaptcha_container recaptcha-container" style={{ width: "auto" }}>
           <p className="captch_info">To continue, type the characters you see in the picture.</p>
           <div className="captch_img_container">
             <div className="captch_img_container_left">
               <div className="captcha_img_wrapper"><img src={captchaImage}/></div>
             </div>
             <div className="captch_img_container_right">
               <div className="captch_refresh">
                 <a id="reload_captcha" className="reload_captcha" href="javascript:void(0)" onClick={fetchCaptcha}>
                   <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                     <g clip-path="url(#clip0_2634_194)">
                       <path d="M7.99996 2.66666V0.666656L5.33329 3.33332L7.99996 5.99999V3.99999C10.2066 3.99999 12 5.79332 12 7.99999C12 8.67332 11.8333 9.31332 11.5333 9.86666L12.5066 10.84C13.0266 10.02 13.3333 9.04666 13.3333 7.99999C13.3333 5.05332 10.9466 2.66666 7.99996 2.66666ZM7.99996 12C5.79329 12 3.99996 10.2067 3.99996 7.99999C3.99996 7.32666 4.16663 6.68666 4.46663 6.13332L3.49329 5.15999C2.97329 5.97999 2.66663 6.95332 2.66663 7.99999C2.66663 10.9467 5.05329 13.3333 7.99996 13.3333V15.3333L10.6666 12.6667L7.99996 9.99999V12Z" fill="#323232"/>
                     </g>
                     <defs>
                       <clipPath id="clip0_2634_194">
                         <rect width="16" height="16" fill="white"/>
                       </clipPath>
                     </defs>
                   </svg>
                 </a>
                
               </div>
             </div>
           </div>
           <div className="captch_input_wrapper">
           <p>The picture contains 8 characters.</p>
           <div className="captch_input">
             <span className="captch_label"><strong>Characters:</strong></span>
             <span className="captch_input_field">
             <input type="text" name="captcha_input" onKeyDown={allowNumberOnly} id="captcha_input" className={captchaError ? 'error_class' : ''}/>
               {captchaError ? (<p className="captch_error">Invalid CAPTCHA. Please try again.</p>) : null}
             </span>
           </div>
         </div>
         </div>
        ) : null}
        <div className={clsx('row mt-4 change_password_action')}>
        <div className='col-auto col-md-6 align-self-center'>
            <span className='custom_cancel_btn' onClick={onCancelUpdate} role="button">Cancel</span>
          </div>
          <div className='col-auto col-md-6'>
            <Button className='custom_save_btn' onClick={onSubmitUpdate} disabled={isDisable()}>Save</Button>
          </div>
        </div>
      </div>
    );
  };

  return (<div className='row px-md-4'>
    <div className='col-12'>
      {/* <BackButton className='mb-3 align-self-start' onBackClick={onCancelUpdate} /> */}
      <Topography.H2 className='custom_h2'>Change Password</Topography.H2>
    </div>
    {renderInputs()}
  </div>);
};